/**
 * Cooperative scheduling for CPU-heavy processing on the main thread.
 *
 * Browsers flag the page as unresponsive ("page is slowing down" / "stop
 * script") when a single task holds the main thread for seconds. These
 * helpers split long loops into time slices and hand control back to the
 * event loop between slices so input, rendering, and the responsiveness
 * watchdog all keep running.
 */

// One macrotask-based yield. Prefers scheduler.yield() (Chrome), which
// resumes with priority over other queued work; falls back to a
// MessageChannel round-trip, which — unlike setTimeout(0) — is not clamped
// to 4ms once timeouts nest.
let pendingYieldResolvers = null;
let yieldChannel = null;

export function yieldToMain() {
  if (typeof globalThis.scheduler?.yield === 'function') {
    return globalThis.scheduler.yield();
  }
  if (typeof MessageChannel !== 'undefined') {
    if (!yieldChannel) {
      yieldChannel = new MessageChannel();
      pendingYieldResolvers = [];
      yieldChannel.port1.onmessage = () => {
        const resolvers = pendingYieldResolvers;
        pendingYieldResolvers = [];
        resolvers.forEach(resolve => resolve());
      };
    }
    return new Promise(resolve => {
      if (pendingYieldResolvers.length === 0) yieldChannel.port2.postMessage(null);
      pendingYieldResolvers.push(resolve);
    });
  }
  return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Yield until the browser has had a chance to paint. yieldToMain() only
 * guarantees a task boundary, and the browser may skip rendering between two
 * back-to-back tasks; waiting for an animation frame and then one more task
 * guarantees the frame was committed. Use before a long synchronous block
 * (e.g. DOMParser on a large file) so a just-shown progress indicator is
 * actually visible while it runs.
 */
export function yieldToPaint() {
  if (typeof requestAnimationFrame !== 'function') return yieldToMain();
  return new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
}

// Default continuous-work budget per slice. 30ms keeps every task well under
// the browser's 50ms long-task threshold while paying the yield cost rarely.
const DEFAULT_BUDGET_MS = 30;

/**
 * Run fn(item, index) over every item, yielding to the event loop whenever
 * the current slice has consumed `budgetMs` of continuous work.
 * `onProgress(done, total)` fires at each yield and once at completion.
 */
export async function forEachChunked(items, fn, { budgetMs = DEFAULT_BUDGET_MS, onProgress } = {}) {
  const total = items.length;
  let sliceStart = performance.now();
  for (let i = 0; i < total; i++) {
    fn(items[i], i);
    if (performance.now() - sliceStart >= budgetMs) {
      if (onProgress) onProgress(i + 1, total);
      await yieldToMain();
      sliceStart = performance.now();
    }
  }
  if (onProgress) onProgress(total, total);
}

/**
 * Time-sliced work loop for non-array iteration (tree walks with an explicit
 * stack, generators, …). Calls step() repeatedly; step() returns false when
 * there is no more work. Yields on the same budget as forEachChunked.
 */
export async function runSliced(step, { budgetMs = DEFAULT_BUDGET_MS, onYield } = {}) {
  let sliceStart = performance.now();
  while (step()) {
    if (performance.now() - sliceStart >= budgetMs) {
      if (onYield) onYield();
      await yieldToMain();
      sliceStart = performance.now();
    }
  }
}
